import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import axiosInstance, { BASE_URL } from "../../../utils/config/config";
import { compressImage } from "../../../utils/compressImage";

export const useBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axiosInstance.get("blog/mine");
      setPosts(data || []);
    } catch (e) {
      setPosts([]);
      setError(e?.response?.data?.message || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPost = useCallback((payload) => axiosInstance.post("blog", payload).then(r => r.data), []);
  const updatePost = useCallback((id, payload) => axiosInstance.put(`blog/${id}`, payload).then(r => r.data), []);
  const submitPost = useCallback((id) => axiosInstance.post(`blog/${id}/submit`).then(r => r.data), []);
  const deletePost = useCallback((id) => axiosInstance.delete(`blog/${id}`), []);
  // Authors may pull their own live post; it returns to their drafts.
  const unpublishPost = useCallback((id) => axiosInstance.post(`blog/${id}/unpublish`), []);

  const uploadCover = useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", await compressImage(file));
    const res = await axios.post(`${BASE_URL}aws/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data?.fileUrl || null;
  }, []);

  return { posts, loading, error, load, createPost, updatePost, submitPost, deletePost, unpublishPost, uploadCover };
};

// Category list as shown to an author (plain names, admin-managed).
export const useCategories = () => {
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("blog/categories");
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  return { categories, loadCategories };
};

// Admin CRUD over the category list (name + how many posts use it).
export const useCategoryAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("blog/admin/categories");
      setCategories(data || []);
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const createCategory = useCallback((name) => axiosInstance.post("blog/admin/categories", { name }), []);
  const renameCategory = useCallback((id, name) => axiosInstance.put(`blog/admin/categories/${id}`, { name }), []);
  const deleteCategory = useCallback((id) => axiosInstance.delete(`blog/admin/categories/${id}`), []);

  return { categories, loading, load, createCategory, renameCategory, deleteCategory };
};
